import React from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_IMAGES, CATEGORIES, BUNDLES } from '../data/mock';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/site/ProductCard';
import InstagramFeed from '../components/site/InstagramFeed';

export default function HomePage() {
  const { products } = useProducts();
  const featured = products.slice(0, 4);
  const bundles = BUNDLES.slice(0, 3);
  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative">
        <div className="grid min-h-[80vh] grid-cols-2">
          <div className="relative overflow-hidden">
            <img src={PRODUCT_IMAGES.heroMen} alt="Man wearing a gold chain" className="h-full w-full object-cover object-[center_center]" />
            <div className="absolute inset-0 bg-gradient-to-r from-espresso/50 to-transparent" />
          </div>
          <div className="relative overflow-hidden">
            <img src={PRODUCT_IMAGES.heroWomen} alt="Woman wearing gold hoops" className="h-full w-full object-cover object-[center_35%]" />
            <div className="absolute inset-0 bg-gradient-to-l from-champagne/20 to-transparent" />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="pointer-events-auto max-w-3xl px-6 text-center text-ivory">
            <p className="eyebrow text-gold">Everyday Luxury · Individual Expression</p>
            <h1 className="mt-4 font-display text-5xl leading-[1.05] md:text-7xl">Your Style. Your Glow.<br/>Your Everyday.</h1>
            <p className="mx-auto mt-6 max-w-xl text-sm text-ivory/90">Discover a world of skincare, grooming, beauty, and timeless jewelry. Thoughtfully curated for everyone.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/men" className="btn btn-primary">Shop Men</Link>
              <Link to="/women" className="btn btn-outline">Shop Women</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP YOUR STYLE */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <p className="eyebrow text-center text-espresso/60">Shop your style</p>
        <h2 className="mt-3 text-center font-display text-4xl md:text-5xl">Two edits. One house.</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {[{t:'Men',img:PRODUCT_IMAGES.heroMen,h:'Elevate Your Everyday.',d:'Skincare, grooming essentials, and timeless accessories designed for the modern man.',to:'/men',cta:'Shop Men'},
            {t:'Women',img:PRODUCT_IMAGES.heroWomen,h:'Your Everyday, Elevated.',d:'Beauty essentials, nourishing skincare, self-care rituals, and elegant jewelry.',to:'/women',cta:'Shop Women'}].map((s) => (
            <Link key={s.t} to={s.to} className="group relative block overflow-hidden">
              <div className="aspect-[4/5] overflow-hidden md:aspect-[5/6]">
                <img src={s.img} alt={s.t} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-espresso/10 to-transparent" />
              <div className="absolute bottom-0 left-0 w-full p-8 text-ivory">
                <p className="eyebrow text-gold">{s.t}</p>
                <h3 className="mt-2 font-display text-3xl md:text-4xl">{s.h}</h3>
                <p className="mt-2 max-w-md text-sm text-ivory/85">{s.d}</p>
                <span className="mt-5 inline-block border-b border-gold pb-1 text-[11px] tracking-[0.18em] text-gold">{s.cta.toUpperCase()}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <p className="eyebrow text-center text-espresso/60">Shop by category</p>
        <h2 className="mt-3 text-center font-display text-4xl md:text-5xl">Four rituals, one house.</h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(CATEGORIES).map(([key, c]) => (
            <Link key={key} to={`/shop?category=${key}`} className="group block">
              <div className="aspect-[4/5] overflow-hidden bg-champagne">
                <img src={PRODUCT_IMAGES[`cat${key.charAt(0).toUpperCase()+key.slice(1)}`]} alt={c.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="mt-4">
                <h3 className="font-display text-xl">{c.label}</h3>
                <p className="text-sm text-espresso/60">{c.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <p className="eyebrow text-espresso/60">New arrivals</p>
            <h2 className="mt-2 font-display text-4xl md:text-5xl">Quietly beautiful.</h2>
          </div>
          <Link to="/shop" className="eyebrow hidden hover:text-gold md:block">Shop all</Link>
        </div>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* UNISEX */}
      <section className="relative mt-10 overflow-hidden bg-espresso text-ivory">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-24 md:grid-cols-2 md:items-center lg:px-8">
          <div>
            <p className="eyebrow text-gold">Made for everyone</p>
            <h2 className="mt-4 font-display text-5xl md:text-6xl">Made for Everyone. <em className="text-gold not-italic">Styled by You.</em></h2>
            <p className="mt-5 max-w-md text-ivory/80">Discover thoughtfully selected essentials that go beyond labels. Beauty, care and style — for everyone.</p>
            <Link to="/unisex" className="mt-8 btn btn-primary inline-flex">Unisex collection</Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={PRODUCT_IMAGES.catJewelry} alt="Jewelry" className="aspect-[3/4] w-full object-cover" />
            <img src={PRODUCT_IMAGES.catSelfcare} alt="Self-care" className="mt-10 aspect-[3/4] w-full object-cover" />
          </div>
        </div>
      </section>

      {/* BUNDLES */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <p className="eyebrow text-center text-espresso/60">Curated bundles</p>
        <h2 className="mt-3 text-center font-display text-4xl md:text-5xl">Better together.</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {bundles.map((b) => (
            <Link key={b.id} to={`/bundles`} className="group block border border-stone-warm bg-ivory p-4 transition hover:border-gold">
              <div className="aspect-square overflow-hidden bg-champagne">
                <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <h3 className="mt-5 font-display text-xl">{b.title}</h3>
              <p className="mt-1 text-xs text-espresso/60">{b.items.join(' · ')}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm">R {b.price}</span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-gold">Save R {b.saves}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="bg-champagne">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-24 md:grid-cols-2 md:items-center lg:px-8">
          <div>
            <p className="eyebrow text-espresso/70">Our philosophy</p>
            <h2 className="mt-4 font-display text-5xl leading-[1.05] md:text-6xl">Self-Care Has No Labels.</h2>
          </div>
          <div>
            <p className="text-espresso/80">We believe confidence starts with how you feel about yourself. Quality skincare, grooming and jewelry — thoughtfully made and gently priced — for everyone.</p>
            <Link to="/about" className="mt-6 inline-block border-b border-espresso pb-1 text-[11px] tracking-[0.18em] text-espresso">OUR STORY</Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramFeed />
    </div>
  );
}
